import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import {
  hasNoLiveServer,
  regionLabel,
  regionOptions,
  useAgentServerRegions,
} from "@/hooks/useAgentServerRegions";
import {
  NoLiveServerNotice,
  RegionsLoadFailedNote,
} from "@/components/agent-server-regions/RegionNotices";
import { supabase } from "../../../supabase/supabase";

const NO_REGION = "__none__";

interface DefaultRegionSettingProps {
  organizationId: string;
  // organizations.default_agent_server_region as loaded: null or "" means none.
  savedRegion: string | null;
  onSaved: (region: string | null) => void;
}

// The region used for V2 calls of assistants that have none of their own.
export default function DefaultRegionSetting({
  organizationId,
  savedRegion,
  onSaved,
}: DefaultRegionSettingProps) {
  const { liveRegions, loaded, loadFailed } = useAgentServerRegions();
  const saved = savedRegion || "";
  const [value, setValue] = useState(saved);
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    setIsSaving(true);
    try {
      // Row level security lets only organization admins update the row. A blocked
      // update is not an error: it changes no row, so ask for the rows back.
      const { data, error } = await supabase
        .from("organizations")
        .update({ default_agent_server_region: value || null })
        .eq("id", organizationId)
        .select("id, default_agent_server_region");

      if (error) throw error;

      if (!data || data.length === 0) {
        setValue(saved);
        toast({
          title: "Not saved",
          description: "Only an organization admin can change this.",
          variant: "destructive",
        });
        return;
      }

      onSaved(data[0].default_agent_server_region ?? null);
      toast({
        title: "Default region saved",
        description: value
          ? `Assistants with no region of their own now use ${regionLabel(value)}.`
          : "The organization no longer has a default region.",
      });
    } catch (error: any) {
      console.error("Error saving default region:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save the default region",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="text-lg font-medium">Default Region</h3>
      <p className="text-sm text-muted-foreground">
        The region whose agent server places V2 calls for assistants that have no
        region of their own. V1 calls ignore it. With no default region, those calls
        go to the default server.
      </p>
      <div className="flex items-center gap-2 max-w-md">
        <Select
          value={value || NO_REGION}
          onValueChange={(v) => setValue(v === NO_REGION ? "" : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="No default region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_REGION}>No default region</SelectItem>
            {regionOptions(liveRegions, saved).map((code) => (
              <SelectItem key={code} value={code}>
                {regionLabel(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={save} disabled={isSaving || value === saved}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
      {loadFailed && <RegionsLoadFailedNote />}
      {hasNoLiveServer(loaded, liveRegions, value) && (
        <NoLiveServerNotice
          region={value}
          consequence="V2 calls that use this default will fail"
        />
      )}
    </div>
  );
}
